import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import MarkCompletedDialog from "./MarkCompletedDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">{label}</p>
      <p className="text-sm text-slate-800 font-medium mt-0.5">{value ?? "—"}</p>
    </div>
  );
}

export default function PileDetailPanel({ pile: p, onClose, onEdit, canDeactivate, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reverting, setReverting] = useState(false);

  const deactivate = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/pile-records/${p.id}/deactivate`);
      toast.success("Pile Record deactivated");
      onUpdated(res.data);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      const res = await client.post(`/pile-records/${p.id}/revert-to-in-progress`);
      toast.success("Pile reverted to In Progress");
      onUpdated(res.data);
      setReverting(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const isCompleted = p.status === "Completed";
  const hasStratum = p.stratum_soil_from != null || p.stratum_weathered_rock_from != null || p.stratum_hard_rock_from != null;

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="pile-detail-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            {p.pile_id_display}
            <Badge className={p.status === "Completed" ? "bg-emerald-600" : "bg-amber-500"}>{p.status}</Badge>
            {p.entered_by_office && (
              <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50 text-xs" data-testid="pile-detail-office-badge">
                Entered by Office
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {p.site_display_label || p.site} · Chainage {p.chainage}
          </SheetDescription>
          <div className="flex gap-2 pt-2">
            <Button size="sm" data-testid="pile-detail-edit-button" onClick={() => onEdit(p)} className="bg-amber-600 hover:bg-amber-700">
              Edit
            </Button>
            {p.active !== false && !isCompleted && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="pile-detail-mark-completed-button" onClick={() => setCompleting(true)}>
                Mark Completed
              </Button>
            )}
            {p.active !== false && isCompleted && (
              <Button size="sm" variant="outline" data-testid="pile-detail-revert-button" onClick={() => setReverting(true)}>
                Revert to In Progress
              </Button>
            )}
            {canDeactivate && p.active !== false && (
              <Button size="sm" variant="destructive" disabled={busy} data-testid="pile-detail-deactivate-button" onClick={deactivate}>
                Deactivate
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="grid grid-cols-2 gap-4 pt-6">
          <Field label="Date" value={p.date ? new Date(p.date).toLocaleDateString("en-IN") : null} />
          <Field label="Entered By" value={p.created_by_name} />
          <Field label="Machine" value={p.machine_label} />
          <Field label="Diameter (mm)" value={p.diameter_mm} />
          <Field label="Achieved Depth (m)" value={p.achieved_depth_m} />
          {isCompleted && <Field label="Completion Date" value={p.completion_date ? new Date(p.completion_date).toLocaleDateString("en-IN") : null} />}
          <Field label="Cage Lowering By" value={p.cage_lowering_by} />
          <Field label="Cage Length (m)" value={p.cage_length} />
          <Field label="Cage Weight (kg)" value={p.cage_weight_kg} />
          <Field label="Concreting By" value={p.concreting_by} />
          <Field label="Concrete Qty (m³)" value={p.concrete_quantity_m3} />
          <Field label="Liner Length (m)" value={p.liner_length_m} />
          <Field label="OGL" value={p.ogl} />
          <Field label="FL" value={p.fl} />
          <Field label="CTL" value={p.ctl} />
          <Field label="COL" value={p.col} />
        </div>

        {hasStratum && (
          <div className="pt-6 border-t border-slate-200 mt-4">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono mb-2">Stratum</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <Field label="Soil" value={p.stratum_soil_from != null ? `${p.stratum_soil_from} – ${p.stratum_soil_to ?? "—"}` : null} />
              <Field
                label="Weathered Rock"
                value={p.stratum_weathered_rock_from != null ? `${p.stratum_weathered_rock_from} – ${p.stratum_weathered_rock_to ?? "—"}` : null}
              />
              <Field label="Hard Rock" value={p.stratum_hard_rock_from != null ? `${p.stratum_hard_rock_from} – ${p.stratum_hard_rock_to ?? "—"}` : null} />
            </div>
          </div>
        )}

        {p.remarks && (
          <div className="pt-4">
            <Field label="Remarks" value={p.remarks} />
          </div>
        )}

        <div className="pt-6 border-t border-slate-200 mt-4">
          <p className="text-xs uppercase tracking-wide text-slate-400 font-mono mb-2">Status History</p>
          <div className="space-y-2" data-testid="pile-status-log">
            {(p.status_log || []).map((log, i) => (
              <div key={i} className="text-xs text-slate-600 flex justify-between border-b border-slate-100 pb-1.5">
                <span>{log.from_status ? `${log.from_status} → ${log.to_status}` : `Created as ${log.to_status}`}</span>
                <span className="text-slate-400">
                  {log.changed_by_name} · {new Date(log.changed_at).toLocaleString("en-IN")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </SheetContent>
      {completing && (
        <MarkCompletedDialog
          pile={p}
          onClose={() => setCompleting(false)}
          onDone={(updated) => {
            setCompleting(false);
            onUpdated(updated);
          }}
        />
      )}
      <AlertDialog open={reverting} onOpenChange={setReverting}>
        <AlertDialogContent data-testid="pile-revert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to In Progress?</AlertDialogTitle>
            <AlertDialogDescription>Pile {p.pile_id_display} will move back from Completed to In Progress. The change is added to the status history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pile-revert-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} data-testid="pile-revert-confirm-button" onClick={(e) => { e.preventDefault(); revert(); }}>
              Revert
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
